import classNames from 'classnames'
import { RichText } from 'marketing-site/src/library/elements/RichText'
import { Text } from 'marketing-site/src/library/elements/Text'
import { CTA } from 'marketing-site/src/transformers/CTA'
import React, { useEffect, useRef, useState } from 'react'
import Slider from 'react-slick'
import { ImageOrAutoplayVideo } from '../../elements/ImageOrAutoplayVideo'
import { Color, fontTTNormsPro, mq } from '../../utils'
import { useSpacing } from 'marketing-site/src/library/providers/spacingProvider'
import { IProps } from './index'

export function Carousel(props: IProps): React.ReactElement {
  const initialSlideNumber = (props.firstSlide ?? 1) - 1

  const [mounted, setMounted] = useState(false)
  const [currentSlide, setCurrentSlide] = useState(initialSlideNumber)
  const slider = useRef<Slider>(null)
  const controlsSlider = useRef<Slider>(null)
  const spacing = useSpacing()

  useEffect(() => {
    // Slick can't calculate correctly size if we init it before page load
    setMounted(true)
  }, [])

  const sliderSettings = {
    arrows: false,
    dots: true,
    infinite: false,
    speed: 200,
    centerMode: true,
    centerPadding: '25%',
    initialSlide: initialSlideNumber,
    asNavFor: controlsSlider.current || undefined,
    beforeChange: (currentSlide: number, nextSlide: number) => {
      setCurrentSlide(nextSlide)
      controlsSlider.current?.slickGoTo(nextSlide)
    },
    ...(props.isMobile && {
      centerMode: false,
      slidesToShow: 1,
    }),
  }

  const controlsSettings = {
    arrows: false,
    dots: false,
    infinite: false,
    speed: 200,
    initialSlide: initialSlideNumber,
    slidesToShow: 3,
    asNavFor: slider.current || undefined,

    ...(props.isMobile && {
      centerMode: true,
      centerPadding: '30px',
      slidesToShow: 1,
    }),
  }

  const carouselClasses = classNames({
    'carousel-parts': true,
    'controls-on-top': props.slidesPosition === 'Top',
  })

  const theme = (props.bgColor ?? 'White').toLowerCase()

  const padding = classNames({
    'section--carousel--padding': !spacing,
  })

  return (
    <>
      <section className={`section--carousel section-theme--${theme} ${padding}`}>
        <div className="carousel-content">
          {props.eyebrow && (
            <p className="eyebrows">
              <Text brand="default" size="eyebrows">
                {props.eyebrow}
              </Text>
            </p>
          )}

          {props.headline && (
            <h2>
              <Text brand="default" size="heading-2">
                {props.headline}
              </Text>
            </h2>
          )}

          {props.subheading && (
            <p className="subheading">
              <Text brand="default" size="heading-4">
                {props.subheading}
              </Text>
            </p>
          )}

          {props.ctaLink && (
            <div className="cta-link-section">
              <CTA {...props.ctaLink} />
            </div>
          )}

          {props.footerText && (
            <p className="footer-text">
              <Text brand="default" size="caption">
                {props.footerText}
              </Text>
            </p>
          )}

          <section className={carouselClasses}>
            <div className="carousel-main">
              <Slider ref={slider} {...sliderSettings}>
                {props.slides.map((slide, index) => (
                  <div key={slide.id}>
                    {/* eslint-disable-next-line */}
                    <div
                      className="carousel-slide"
                      data-testid="carousel-slide"
                      style={{
                        backgroundColor: slide.backgroundColor || 'transparent',
                      }}
                      onClick={() => {
                        slider.current?.slickGoTo(index)
                        controlsSlider.current?.slickGoTo(index)
                      }}
                    >
                      <div
                        className={`carousel-image-wrap carousel-image-wrap--${index}`}
                        style={{ maxWidth: slide.imageWidth || 'none', margin: '0 auto' }}
                      >
                        <ImageOrAutoplayVideo
                          video={slide.video}
                          imageRef={slide.image}
                          forcedAnimationState={currentSlide === index ? 'play' : 'stop'}
                        />
                      </div>
                    </div>
                  </div>
                ))}
              </Slider>
            </div>

            {mounted && (
              <div className="carousel-controls">
                <Slider ref={controlsSlider} {...controlsSettings}>
                  {props.slides.map(
                    (slide, index) =>
                      slide.description && (
                        <div key={slide.id}>
                          {/* eslint-disable-next-line */}
                          <div
                            onClick={() => {
                              slider.current?.slickGoTo(index)
                              controlsSlider.current?.slickGoTo(index)
                            }}
                            className={`carousel-description ${
                              currentSlide === index ? 'carousel-description-active' : null
                            }`}
                          >
                            <RichText document={slide.description} />
                          </div>
                        </div>
                      ),
                  )}
                </Slider>
              </div>
            )}
          </section>
        </div>
      </section>

      <style jsx>{`
        .footer-text {
          color: ${Color.Slate};
          opacity: 0.6;
          text-align: center;
          margin-top: 16px;
        }
        .cta-link-section {
          display: flex;
          justify-content: center;
          margin-top: 48px;
          :global(.cta-link .button-content span) {
            font-weight: 400;
            b {
              font-weight: 700;
            }
          }
        }
        .carousel-parts {
          display: flex;
          flex-direction: column;
          max-width: 2000px;
          margin: 4rem auto 0;

          &.controls-on-top {
            flex-direction: column-reverse;

            .carousel-controls {
              margin: 0 auto 64px;
            }
          }
        }
        .eyebrows {
          text-align: center;
          margin-bottom: 20px;
        }

        .subheading {
          margin-top: 24px;
          text-align: center;
        }
        .section--carousel {
          min-width: 100%;
          padding-left: 0;
          padding-right: 0;

          &--padding {
            padding-top: 6.5rem;
            padding-bottom: 6.5rem;
          }

          &.section-theme {
            &--white {
              background: ${Color.White};
            }

            &--lightgray {
              background: ${Color.Stone};
            }
          }
        }

        .carousel-content {
          max-width: 100%;
          margin: 0 auto;
        }

        h2 {
          text-align: center;
        }

        .carousel-slide {
          margin: 0 20px;
          text-align: center;
          cursor: pointer;
          border-radius: 25px;
          overflow: hidden;
          max-height: 600px;

          img {
            max-height: 600px;
            margin: 0 auto;
          }
        }

        :global(.slick-active) .carousel-slide {
          cursor: default;
        }

        .section--carousel :global(.slick-dots) {
          margin: 20px auto 0;
          text-align: center;
        }

        .section--carousel :global(.slick-dots li) {
          display: inline-block;
        }

        .section--carousel :global(.slick-dots button) {
          width: 8px;
          height: 8px;
          border-radius: 50%;
          background: ${Color.Slate};
          font-size: 0;
          margin: 0 5px;
          cursor: pointer;
        }

        .section--carousel :global(.slick-dots .slick-active button) {
          opacity: 0.3;
        }

        .carousel-controls {
          margin: 64px auto 0;
          max-width: 1040px;
          width: 100%;
        }

        .carousel-description {
          border: 2px solid ${Color.Slate};
          border-radius: 8px;
          padding: 15px;
          margin: 0 10px;
          cursor: pointer;
          transition: 0.2s ease-out;
          transition-property: color, background, border-color;
        }

        .carousel-description-active,
        .carousel-description:hover {
          background: ${Color.White};
          color: ${Color.Slate};
          border-color: ${Color.White};
        }

        .carousel-description :global(p) {
          margin: 0;
        }

        .carousel-description :global(b) {
          font-family: ${fontTTNormsPro};
        }

        @media (max-width: 767px) {
          .carousel-slide {
            background: none !important;
          }
        }

        @media (${mq.mobile}) {
          // set equal height of slick slides
          :global(.slick-track) {
            display: flex;
            align-items: stretch;
            :global(.slick-slide) {
              height: initial;
              float: none;
              :global(> div) {
                height: 100%;
                > div {
                  height: 100%;

                  :global(.carousel-description) {
                    height: 100%;
                  }
                }
              }
            }
          }
        }

        @media (${mq.tablet}) {
          // set equal height of slick slides
          :global(.slick-track) {
            display: flex;
            align-items: stretch;
            :global(.slick-slide) {
              height: initial;
              float: none;
              :global(> div) {
                height: 100%;
                > div {
                  height: 100%;

                  :global(.carousel-description) {
                    height: 100%;
                  }
                }
              }
            }
          }
          .carousel-slide {
            padding: 0 40px;
            margin: 0 20px;
          }

          .carousel-description {
            padding: 15px 30px 15px 20px;
            max-width: 340px;
            width: auto;
          }

          .carousel-controls {
            padding: 0 20px;
            margin-top: 60px;
          }
        }

        @media (${mq.desktop}) {
          .carousel-slide {
            margin: 0 45px;
          }
        }

        @media (min-width: 1290px) {
          .carousel-main {
            grid-area: slider;
          }
        }
      `}</style>
    </>
  )
}
